// src/controllers/services/leadService.js
import { LEAD_STATUS } from '../../models/leadTypes';
import { supabase } from './supabase';

export const saveLead = async (leadData) => {
  try {
    const newLead = {
      name: leadData.name || 'Newsletter Subscriber',
      mobile: leadData.mobile || 'N/A',
      city: leadData.city || 'N/A',
      // The contact form sends its text as `message`; it used to be dropped.
      requirement: leadData.requirement || leadData.message || (leadData.email ? `Email: ${leadData.email}` : ''),
      source: leadData.source,
      type: leadData.type,
      status: LEAD_STATUS.NEW
    };
    
    if (leadData.email) {
      newLead.email = leadData.email;
    }
    
    // Attach the signed-in customer, so they can be told when our team
    // updates the request.
    const { data: sessionData } = await supabase.auth.getSession();
    const userId = sessionData?.session?.user?.id;
    if (userId) newLead.user_id = userId;

    // No `.select()` here: reading leads back is limited to admins and the
    // lead's own customer, and a signed-out visitor is neither.
    let res = await supabase.from('leads').insert([newLead]);

    // Fallback if 'email' column doesn't exist in Supabase schema
    if (res.error && leadData.email && res.error.message?.includes('email')) {
      delete newLead.email;
      res = await supabase.from('leads').insert([newLead]);
    }

    if (res.error) throw res.error;
    return { success: true, lead: newLead };
  } catch (error) {
    console.error('Error saving lead to Supabase:', error);
    return { success: false, error };
  }
};

export const saveSolutionLead = async (leadData) => {
  try {
    // 1. Honeypot anti-spam check
    if (leadData.honeypot && leadData.honeypot.trim() !== '') {
      console.warn('Spam submission detected and blocked.');
      return { success: true, isBot: true };
    }

    // 2. Validate mobile number
    const cleanPhone = String(leadData.mobile || '').replace(/\D/g, '');
    const validMobile = cleanPhone.startsWith('91') && cleanPhone.length === 12
      ? cleanPhone.slice(2)
      : cleanPhone;

    if (!/^[6-9]\d{9}$/.test(validMobile)) {
      throw new Error('Please enter a valid 10-digit mobile number.');
    }

    if (!leadData.name || leadData.name.trim().length < 2) {
      throw new Error('Please enter your full name.');
    }

    // 3. User session if signed in
    const { data: sessionData } = await supabase.auth.getSession();
    const userId = sessionData?.session?.user?.id || null;

    const record = {
      name: leadData.name.trim(),
      mobile: validMobile,
      requirement: leadData.requirement || 'Buy a Property',
      city: leadData.city || 'Ghazipur',
      property_type: leadData.property_type || null,
      budget: leadData.budget || null,
      details: leadData.details ? leadData.details.trim() : null,
      source: leadData.source || 'website_modal',
      status: 'New',
      user_id: userId,
    };

    let { error } = await supabase
      .from('property_solution_leads')
      .insert([record]);

    // If property_solution_leads is missing in database schema, seamlessly route into central `leads` table
    if (error && (error.code === 'PGRST205' || error.message?.includes('schema cache') || error.message?.includes('does not exist'))) {
      const reqDetails = [
        leadData.requirement || 'Buy a Property',
        leadData.property_type ? `Type: ${leadData.property_type}` : null,
        leadData.budget ? `Budget: ${leadData.budget}` : null,
        leadData.details ? `Details: ${leadData.details}` : null
      ].filter(Boolean).join(' | ');

      const fallbackType = String(leadData.requirement || '').toLowerCase().includes('sell') ? 'seller' : 'buyer';

      const fallbackRecord = {
        name: leadData.name.trim(),
        mobile: validMobile,
        requirement: reqDetails,
        type: fallbackType,
        city: leadData.city || 'Ghazipur',
        source: leadData.source || 'website_modal',
        status: 'New',
        user_id: userId
      };

      const fallbackRes = await supabase.from('leads').insert([fallbackRecord]);
      if (!fallbackRes.error) {
        error = null;
      }
    }

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error saving property solution lead:', error);
    return { success: false, error: error.message || 'Failed to submit' };
  }
};

export const getLeads = async () => {
  try {
    const { data, error } = await supabase
      .from('leads')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    // Map db created_at to createdAt expected by UI components
    return (data || []).map(item => ({
      ...item,
      createdAt: item.created_at
    }));
  } catch (error) {
    console.error('Error getting leads from Supabase:', error);
    return [];
  }
};

export const updateLeadStatus = async (leadId, newStatus) => {
  try {
    const { error } = await supabase
      .from('leads')
      .update({ status: newStatus })
      .eq('id', leadId);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error updating lead status in Supabase:', error);
    return { success: false, error };
  }
};

export const submitPropertyEnquiry = async ({
  propertyId,
  buyerId = null,
  buyerName,
  buyerPhone,
  message,
  source = 'web',
  consent = true
}) => {
  try {
    // 1. Consent validation (Rule 9)
    if (!consent) {
      return {
        success: false,
        error: 'Main sahmat hoon ki meri details share ki jayein checkbox par click karna zaroori hai.'
      };
    }

    // 2. Mobile validation
    const cleanPhone = String(buyerPhone || '').replace(/\D/g, '');
    const validMobile = cleanPhone.startsWith('91') && cleanPhone.length === 12
      ? cleanPhone.slice(2)
      : cleanPhone;

    if (!/^[6-9]\d{9}$/.test(validMobile)) {
      return {
        success: false,
        error: 'Kripya ek valid 10-digit Indian mobile number darj karein (starting with 6-9).'
      };
    }

    const cleanName = (buyerName || '').trim();
    if (cleanName.length < 2) {
      return {
        success: false,
        error: 'Kripya apna poora naam darj karein.'
      };
    }

    // 3. Try RPC submit_property_enquiry first for server-side anti-spam check
    try {
      const { data: rpcData, error: rpcError } = await supabase.rpc('submit_property_enquiry', {
        p_property_id: propertyId,
        p_buyer_id: buyerId,
        p_buyer_name: cleanName,
        p_buyer_phone: validMobile,
        p_message: message || 'Interested in this property site visit.',
        p_source: source,
        p_consent: true
      });

      if (!rpcError && rpcData) {
        if (rpcData.success) {
          return { success: true, enquiryId: rpcData.enquiry_id };
        } else {
          return { success: false, error: rpcData.message };
        }
      }
    } catch (e) {
      console.warn('RPC submit_property_enquiry fallback to direct insert:', e);
    }

    // 4. Fallback Direct Insert if RPC not present in remote DB
    const { data, error } = await supabase
      .from('enquiries')
      .insert({
        property_id: propertyId,
        buyer_id: buyerId,
        buyer_name: cleanName,
        buyer_phone: validMobile,
        message: message || 'Interested in this property.',
        source: source,
        consent_given: true,
        status: 'New'
      })
      .select('id')
      .single();

    if (error) throw error;
    return { success: true, enquiryId: data?.id };
  } catch (err) {
    console.error('Error submitting property enquiry:', err);
    return { success: false, error: err.message || 'Failed to submit enquiry' };
  }
};

export const updateOwnerEnquiry = async (enquiryId, { status, ownerNotes }) => {
  try {
    const updatePayload = { updated_at: new Date().toISOString() };
    if (status) updatePayload.status = status;
    if (ownerNotes !== undefined) updatePayload.owner_notes = ownerNotes;

    const { error } = await supabase
      .from('enquiries')
      .update(updatePayload)
      .eq('id', enquiryId);

    if (error) throw error;
    return { success: true };
  } catch (err) {
    console.error('Error updating owner enquiry:', err);
    return { success: false, error: err.message || 'Failed to update' };
  }
};

export const DEFAULT_WHATSAPP_PHONE = '9214901596';

let activeContactInfo = {
  support_phone: DEFAULT_WHATSAPP_PHONE,
  support_email: 'team@nobrokerages.online',
};

/** Keep WhatsApp/email helpers in sync with admin-managed site settings. */
export const setActiveContactInfo = (contactInfo = {}) => {
  const digits = String(contactInfo.support_phone || DEFAULT_WHATSAPP_PHONE).replace(/\D/g, '');
  const phone =
    digits.startsWith('91') && digits.length === 12
      ? digits.slice(2)
      : digits || DEFAULT_WHATSAPP_PHONE;

  activeContactInfo = {
    support_phone: phone,
    support_email: contactInfo.support_email || activeContactInfo.support_email,
  };
};

export const getActiveContactInfo = () => activeContactInfo;

export const generateWhatsAppLink = (message, phone) => {
  const fallback = activeContactInfo.support_phone || DEFAULT_WHATSAPP_PHONE;
  const digits = String(phone || fallback).replace(/\D/g, '');
  const normalized =
    digits.startsWith('91') && digits.length === 12 ? digits.slice(2) : digits || fallback;
  const encodedMessage = encodeURIComponent(message);
  return `https://wa.me/${normalized}?text=${encodedMessage}`;
};
